import ELK, { ElkNode } from 'elkjs/lib/elk.bundled.js';
import { SetRequired } from 'type-fest';

import { Edge, FamilyTreeNode } from '@/components/diagrams/FlowChart';
import {
  ClientProfileRelationshipType,
  MarriageEndReason,
} from '@/types/schema';

import { FamilyTreeState } from '../types';
import {
  CHILDREN_COUPLE_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_1_EX_HANDLE_ID,
  CHILDREN_SPOUSE_1_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID,
  CHILDREN_SPOUSE_2_EX_HANDLE_ID,
  CHILDREN_SPOUSE_2_HANDLE_ID,
  COUPLE_NODE_HEIGHT,
  COUPLE_NODE_WIDTH,
  EX_SPOUSE_CONNECTOR_WIDTH,
  EX_SPOUSE_WIDTH,
  MARRIAGE_GROUP_PREFIX,
  PARENTS_SPOUSE_1_EX_HANDLE_ID,
  PARENTS_SPOUSE_1_HANDLE_ID,
  PARENTS_SPOUSE_2_EX_HANDLE_ID,
  PARENTS_SPOUSE_2_HANDLE_ID,
  PERSON_BOTTOM_HANDLE_ID,
  PERSON_NODE_HEIGHT,
  PERSON_NODE_WIDTH,
  PERSON_TOP_HANDLE_ID,
  SIMPLE_ELK_OPTIONS,
} from './elkLayout.constants';
import { convertGraphQLToLayoutData } from './elkLayout.helpers';
import { applyFamilyTreePostProcessing } from './elkLayoutSimplePostProcessing';
import { filterOrphanedNodes } from './filterOrphanedNodes';

const elk = new ELK();

/**
 * Simplified family tree layout algorithm.
 * Focuses on clean, readable layouts without complex edge case handling.
 *
 * @param state - The family tree state to layout
 */
export async function computeFamilyTreeLayoutSimple(state: FamilyTreeState) {
  const data = convertGraphQLToLayoutData(state);

  // Sort data for deterministic ordering (critical for consistent layouts)
  // Sort persons by ID
  const sortedPersons = [...data.persons].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  // Sort marriages by ID and their internal arrays
  const sortedMarriages = [...data.marriages]
    .map((m) => ({
      ...m,
      spouses: [...m.spouses].sort((a, b) => a.localeCompare(b)),
      children: m.children
        ? [...m.children].sort((a, b) => a.localeCompare(b))
        : undefined,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));

  // Use sorted data
  const sortedData = {
    persons: sortedPersons,
    marriages: sortedMarriages,
  };

  const personMap = new Map(sortedData.persons.map((p) => [p.id, p]));

  // Helper: Parse a person's date of birth into a timestamp, or null if it is
  // missing/unparseable. dateOfBirth is a Time scalar; new Date() handles both
  // a Date value and an ISO string.
  function birthTimestamp(personId: string): number | null {
    const dateOfBirth = personMap.get(personId)?.dateOfBirth;
    if (!dateOfBirth) return null;
    const time = new Date(dateOfBirth).getTime();
    return Number.isNaN(time) ? null : time;
  }

  // Birth timestamp keyed by the LAYOUT node that represents each child (their
  // own person node, or their marriage-group node when the child is married).
  // Feeds the post-processing pass that orders siblings by age (T1-6540).
  const childBirthTimestampByNodeId = new Map<string, number | null>();

  // Helper: Check if a person has a PARENT relationship to a child in clientProfiles data
  // Returns null if no relationship data is available (trust marriage data in that case)
  function isActualParent(personId: string, childId: string): boolean | null {
    if (!state.clientProfiles) return null;

    const person = state.clientProfiles.edges?.find(
      (edge) => edge?.node?.id === personId
    )?.node;

    if (!person) return null;

    // Check if this person has a PARENT relationship to the child
    return (person.relationships || []).some(
      (rel) =>
        rel?.type === ClientProfileRelationshipType.Parent &&
        rel?.toClientProfile?.id === childId
    );
  }

  // Build a map of all marriages for each person
  const personMarriages = new Map<string, typeof sortedData.marriages>();
  sortedData.marriages.forEach((marriage) => {
    marriage.spouses.forEach((spouseId) => {
      const marriages = personMarriages.get(spouseId) || [];
      marriages.push(marriage);
      personMarriages.set(spouseId, marriages);
    });
  });

  // Helper: Check if a marriage represents an ex-spouse relationship (not a current/deceased spouse)
  // Uses the clientProfiles relationship data to determine the actual relationship type
  function isExSpouseMarriage(
    marriage: (typeof sortedData.marriages)[number]
  ): boolean {
    if (!state.clientProfiles || marriage.spouses.length < 2) return false;

    const spouse1Id = marriage.spouses[0];
    const spouse2Id = marriage.spouses[1];

    // Find relationship data for both spouses
    const spouse1Data = state.clientProfiles.edges?.find(
      (edge) => edge?.node?.id === spouse1Id
    )?.node;

    const spouse2Data = state.clientProfiles.edges?.find(
      (edge) => edge?.node?.id === spouse2Id
    )?.node;

    // Check if spouse1 has an EX_SPOUSE relationship to spouse2
    const spouse1HasExSpouseRel =
      spouse1Data?.relationships?.some(
        (rel) =>
          rel?.type === ClientProfileRelationshipType.ExSpouse &&
          rel?.toClientProfile?.id === spouse2Id
      ) ?? false;

    // Check if spouse2 has an EX_SPOUSE relationship to spouse1
    const spouse2HasExSpouseRel =
      spouse2Data?.relationships?.some(
        (rel) =>
          rel?.type === ClientProfileRelationshipType.ExSpouse &&
          rel?.toClientProfile?.id === spouse1Id
      ) ?? false;

    // Return true if either person has the EX_SPOUSE relationship
    // (handles cases where relationship is only recorded on one person)
    return spouse1HasExSpouseRel || spouse2HasExSpouseRel;
  }

  // Track which persons are in marriage groups
  const personsInMarriageGroups = new Set<string>();

  // Create marriage groups for current marriages
  const marriageGroups: Array<{
    id: string;
    spouse1Id: string;
    spouse2Id?: string;
    spouse1ExSpouseIds: string[];
    spouse2ExSpouseIds: string[];
    children: string[];
    hasCoupleChildren?: boolean; // Track if any children use the couple handle
    hasSpouse1ExCoparentChildren?: boolean; // Track if any children use spouse1 ex-spouse co-parent handle
    hasSpouse2ExCoparentChildren?: boolean; // Track if any children use spouse2 ex-spouse co-parent handle
  }> = [];

  // Process current marriages AND deceased spouse marriages
  // BUT exclude marriages that are marked as EX_SPOUSE in the relationship data
  sortedData.marriages.forEach((marriage) => {
    const isCurrentOrDeceased =
      marriage.isCurrent ||
      (!marriage.isCurrent &&
        marriage.endReason === MarriageEndReason.Death &&
        !isExSpouseMarriage(marriage));

    if (isCurrentOrDeceased && marriage.spouses.length >= 2) {
      const spouse1Id = marriage.spouses[0]!;
      const spouse2Id = marriage.spouses[1]!;

      if (personMap.has(spouse1Id) && personMap.has(spouse2Id)) {
        // Find ex-spouses for each spouse
        const spouse1Marriages = personMarriages.get(spouse1Id) || [];
        const spouse2Marriages = personMarriages.get(spouse2Id) || [];

        // Collect ex-spouse IDs (divorced spouses or ex-spouse relationships not in current marriage)
        // Sort for deterministic ordering
        const spouse1ExSpouseIds = spouse1Marriages
          .filter(
            (m) =>
              !m.isCurrent &&
              (m.endReason === MarriageEndReason.Divorced ||
                isExSpouseMarriage(m)) &&
              m.id !== marriage.id
          )
          .flatMap((m) =>
            m.spouses.filter((id) => id !== spouse1Id && personMap.has(id))
          )
          .sort((a, b) => a.localeCompare(b));

        const spouse2ExSpouseIds = spouse2Marriages
          .filter(
            (m) =>
              !m.isCurrent &&
              (m.endReason === MarriageEndReason.Divorced ||
                isExSpouseMarriage(m)) &&
              m.id !== marriage.id
          )
          .flatMap((m) =>
            m.spouses.filter((id) => id !== spouse2Id && personMap.has(id))
          )
          .sort((a, b) => a.localeCompare(b));

        // Collect all children from ex-spouse marriages
        const allChildren = new Set(marriage.children || []);
        spouse1Marriages
          .filter(
            (m) =>
              !m.isCurrent &&
              (m.endReason === MarriageEndReason.Divorced ||
                isExSpouseMarriage(m))
          )
          .forEach((m) => m.children?.forEach((c) => allChildren.add(c)));
        spouse2Marriages
          .filter(
            (m) =>
              !m.isCurrent &&
              (m.endReason === MarriageEndReason.Divorced ||
                isExSpouseMarriage(m))
          )
          .forEach((m) => m.children?.forEach((c) => allChildren.add(c)));

        marriageGroups.push({
          id: `${MARRIAGE_GROUP_PREFIX}${marriage.id}`,
          spouse1Id,
          spouse2Id,
          spouse1ExSpouseIds,
          spouse2ExSpouseIds,
          children: Array.from(allChildren),
          hasCoupleChildren: undefined, // Will be calculated after determining child handles
          hasSpouse1ExCoparentChildren: undefined, // Will be calculated after determining child handles
          hasSpouse2ExCoparentChildren: undefined, // Will be calculated after determining child handles
        });

        personsInMarriageGroups.add(spouse1Id);
        personsInMarriageGroups.add(spouse2Id);
        // Mark ex-spouses as "in marriage groups" so they don't get separate nodes
        spouse1ExSpouseIds.forEach((id) => personsInMarriageGroups.add(id));
        spouse2ExSpouseIds.forEach((id) => personsInMarriageGroups.add(id));
      }
    }
  });

  // Identify people to exclude from tree (ex-spouses' partners)
  // Do this BEFORE creating single-person groups to prevent those groups from being created
  const peopleToExcludeFromTree = new Set<string>();
  marriageGroups.forEach((group) => {
    const allExSpouseIds = [
      ...group.spouse1ExSpouseIds,
      ...group.spouse2ExSpouseIds,
    ];

    allExSpouseIds.forEach((exSpouseId) => {
      const exSpouseMarriages = personMarriages.get(exSpouseId) || [];
      exSpouseMarriages.forEach((marriage) => {
        marriage.spouses.forEach((spouseId) => {
          const person = personMap.get(spouseId);
          // Exclude if:
          // 1. Not the ex-spouse themselves
          // 2. Not already in a current/deceased marriage group
          // 3. NOT a primary client (primary clients should always be shown)
          if (
            spouseId !== exSpouseId &&
            !personsInMarriageGroups.has(spouseId) &&
            !person?.isPrimary
          ) {
            peopleToExcludeFromTree.add(spouseId);
          }
        });
      });
    });
  });

  // Process people with ONLY ex-spouses (no current spouse)
  // These get a single-person marriage group showing their ex-spouses
  const peopleWithOnlyExSpouses = new Set<string>();
  sortedData.marriages.forEach((marriage) => {
    if (
      !marriage.isCurrent &&
      (marriage.endReason === MarriageEndReason.Divorced ||
        isExSpouseMarriage(marriage)) &&
      marriage.spouses.length >= 2
    ) {
      marriage.spouses.forEach((spouseId) => {
        // Only consider if they're not already in a current marriage AND not excluded
        const isInCurrentMarriage = marriageGroups.some(
          (g) => g.spouse1Id === spouseId || g.spouse2Id === spouseId
        );
        if (!isInCurrentMarriage && !peopleToExcludeFromTree.has(spouseId)) {
          peopleWithOnlyExSpouses.add(spouseId);
        }
      });
    }
  });

  // Sort to process primary clients first
  const sortedPeopleWithOnlyExSpouses = Array.from(
    peopleWithOnlyExSpouses
  ).sort((a, b) => {
    const aPerson = personMap.get(a);
    const bPerson = personMap.get(b);
    const aIsPrimary = aPerson?.isPrimary ?? false;
    const bIsPrimary = bPerson?.isPrimary ?? false;

    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return a.localeCompare(b);
  });

  // Create ex-spouse groups for these people
  sortedPeopleWithOnlyExSpouses.forEach((personId) => {
    if (personsInMarriageGroups.has(personId)) return; // Skip if already in a group

    const person = personMap.get(personId);
    if (!person) return;

    const personMarriagesList = personMarriages.get(personId) || [];

    // Collect all divorced ex-spouses or ex-spouse relationships
    // Sort for deterministic ordering
    const exSpouseIds = personMarriagesList
      .filter(
        (m) =>
          !m.isCurrent &&
          (m.endReason === MarriageEndReason.Divorced || isExSpouseMarriage(m))
      )
      .flatMap((m) =>
        m.spouses.filter((id) => id !== personId && personMap.has(id))
      )
      .sort((a, b) => a.localeCompare(b));

    // Collect children from all divorced marriages or ex-spouse relationships
    const allChildren = new Set<string>();
    personMarriagesList
      .filter(
        (m) =>
          !m.isCurrent &&
          (m.endReason === MarriageEndReason.Divorced || isExSpouseMarriage(m))
      )
      .forEach((m) => m.children?.forEach((c) => allChildren.add(c)));

    if (exSpouseIds.length > 0) {
      personsInMarriageGroups.add(personId);
      exSpouseIds.forEach((id) => personsInMarriageGroups.add(id));

      marriageGroups.push({
        id: `${MARRIAGE_GROUP_PREFIX}ex-spouses-${personId}`,
        spouse1Id: personId,
        spouse2Id: undefined, // No current spouse
        spouse1ExSpouseIds: exSpouseIds,
        spouse2ExSpouseIds: [],
        children: Array.from(allChildren),
        hasCoupleChildren: undefined, // Will be calculated after determining child handles
        hasSpouse1ExCoparentChildren: undefined, // Will be calculated after determining child handles
        hasSpouse2ExCoparentChildren: undefined, // Will be calculated after determining child handles
      });
    }
  });

  // Use all marriage groups (pruning already happened during single-person group creation)
  // Sort marriage groups by ID for deterministic ordering
  const prunedMarriageGroups = [...marriageGroups].sort((a, b) =>
    a.id.localeCompare(b.id)
  );

  // Calculate which children use which handles for each group
  // This determines which vertical connectors we show
  prunedMarriageGroups.forEach((group) => {
    let hasCoupleChildren = false;
    let hasSpouse1ExCoparentChildren = false;
    let hasSpouse2ExCoparentChildren = false;

    for (const childId of group.children) {
      const sourceHandle = getSourceHandleForChild(group, childId);
      if (sourceHandle === CHILDREN_COUPLE_HANDLE_ID) {
        hasCoupleChildren = true;
      } else if (sourceHandle === CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID) {
        hasSpouse1ExCoparentChildren = true;
      } else if (sourceHandle === CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID) {
        hasSpouse2ExCoparentChildren = true;
      }
    }

    group.hasCoupleChildren = hasCoupleChildren;
    group.hasSpouse1ExCoparentChildren = hasSpouse1ExCoparentChildren;
    group.hasSpouse2ExCoparentChildren = hasSpouse2ExCoparentChildren;
  });

  // Create ELK graph
  const elkGraph: SetRequired<ElkNode, 'children' | 'edges'> = {
    id: 'root',
    layoutOptions: SIMPLE_ELK_OPTIONS,
    children: [],
    edges: [],
  };

  // Helper: Create unique ELK port IDs that map to React Flow handle IDs
  const getPortId = (nodeId: string, handleId: string) =>
    `${nodeId}::${handleId}`;

  // Helper: Determine which spouse handle to use when child is in a marriage group
  const getTargetHandleForChildInGroup = (
    childGroup: (typeof marriageGroups)[number],
    childId: string
  ): string => {
    // Check if child is spouse1
    if (childGroup.spouse1Id === childId) {
      return PARENTS_SPOUSE_1_HANDLE_ID;
    }
    // Check if child is spouse2
    if (childGroup.spouse2Id === childId) {
      return PARENTS_SPOUSE_2_HANDLE_ID;
    }
    // Check if child is in spouse1's ex-spouse list
    if (childGroup.spouse1ExSpouseIds.includes(childId)) {
      return PARENTS_SPOUSE_1_EX_HANDLE_ID;
    }
    // Check if child is in spouse2's ex-spouse list
    if (childGroup.spouse2ExSpouseIds.includes(childId)) {
      return PARENTS_SPOUSE_2_EX_HANDLE_ID;
    }
    // Fallback to spouse1 handle (shouldn't happen)
    return PARENTS_SPOUSE_1_HANDLE_ID;
  };

  // Add marriage group nodes with proper handles for spouse positions
  prunedMarriageGroups.forEach((group) => {
    // Calculate width based on ex-spouses and whether there's a couple
    // If no spouse2, use PERSON_NODE_WIDTH instead of COUPLE_NODE_WIDTH
    const baseWidth = group.spouse2Id ? COUPLE_NODE_WIDTH : PERSON_NODE_WIDTH;
    const totalExSpouses =
      group.spouse1ExSpouseIds.length + group.spouse2ExSpouseIds.length;
    const width =
      baseWidth +
      totalExSpouses * EX_SPOUSE_WIDTH +
      (group.spouse1ExSpouseIds.length > 0 ? EX_SPOUSE_CONNECTOR_WIDTH : 0) +
      (group.spouse2ExSpouseIds.length > 0 ? EX_SPOUSE_CONNECTOR_WIDTH : 0);

    const ports: Array<{ id: string; layoutOptions: Record<string, string> }> =
      [];
    let northIdx = 0;
    let southIdx = 0;

    // Top (NORTH) target ports: left ex, spouse1, spouse2, right ex
    if (group.spouse1ExSpouseIds.length > 0) {
      ports.push({
        id: getPortId(group.id, PARENTS_SPOUSE_1_EX_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'NORTH',
          'org.eclipse.elk.port.index': String(northIdx++),
        },
      });
    }
    ports.push({
      id: getPortId(group.id, PARENTS_SPOUSE_1_HANDLE_ID),
      layoutOptions: {
        'org.eclipse.elk.port.side': 'NORTH',
        'org.eclipse.elk.port.index': String(northIdx++),
      },
    });
    if (group.spouse2Id) {
      ports.push({
        id: getPortId(group.id, PARENTS_SPOUSE_2_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'NORTH',
          'org.eclipse.elk.port.index': String(northIdx++),
        },
      });
    }
    if (group.spouse2ExSpouseIds.length > 0) {
      ports.push({
        id: getPortId(group.id, PARENTS_SPOUSE_2_EX_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'NORTH',
          'org.eclipse.elk.port.index': String(northIdx++),
        },
      });
    }

    // Bottom (SOUTH) source ports: left ex, left coparent, spouse1, couple, spouse2, right coparent, right ex
    if (group.spouse1ExSpouseIds.length > 0) {
      ports.push({
        id: getPortId(group.id, CHILDREN_SPOUSE_1_EX_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'SOUTH',
          'org.eclipse.elk.port.index': String(southIdx++),
        },
      });
      // Add co-parent handle for single ex-spouse scenario
      if (group.spouse1ExSpouseIds.length === 1) {
        ports.push({
          id: getPortId(group.id, CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID),
          layoutOptions: {
            'org.eclipse.elk.port.side': 'SOUTH',
            'org.eclipse.elk.port.index': String(southIdx++),
          },
        });
      }
    }
    // Individual spouse1 children handle
    ports.push({
      id: getPortId(group.id, CHILDREN_SPOUSE_1_HANDLE_ID),
      layoutOptions: {
        'org.eclipse.elk.port.side': 'SOUTH',
        'org.eclipse.elk.port.index': String(southIdx++),
      },
    });
    // Only add couple handle if there's actually a couple OR children using it
    if (group.spouse2Id || group.hasCoupleChildren) {
      ports.push({
        id: getPortId(group.id, CHILDREN_COUPLE_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'SOUTH',
          'org.eclipse.elk.port.index': String(southIdx++),
        },
      });
    }
    // Individual spouse2 children handle
    if (group.spouse2Id) {
      ports.push({
        id: getPortId(group.id, CHILDREN_SPOUSE_2_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'SOUTH',
          'org.eclipse.elk.port.index': String(southIdx++),
        },
      });
    }
    if (group.spouse2ExSpouseIds.length > 0) {
      // Add co-parent handle for single ex-spouse scenario
      if (group.spouse2ExSpouseIds.length === 1) {
        ports.push({
          id: getPortId(group.id, CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID),
          layoutOptions: {
            'org.eclipse.elk.port.side': 'SOUTH',
            'org.eclipse.elk.port.index': String(southIdx++),
          },
        });
      }
      ports.push({
        id: getPortId(group.id, CHILDREN_SPOUSE_2_EX_HANDLE_ID),
        layoutOptions: {
          'org.eclipse.elk.port.side': 'SOUTH',
          'org.eclipse.elk.port.index': String(southIdx++),
        },
      });
    }

    elkGraph.children.push({
      id: group.id,
      width,
      height: COUPLE_NODE_HEIGHT,
      layoutOptions: {
        'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
      },
      ports,
    });
  });

  // Add individual person nodes (not in marriage groups and not excluded)
  sortedData.persons.forEach((person) => {
    if (
      !personsInMarriageGroups.has(person.id) &&
      !peopleToExcludeFromTree.has(person.id)
    ) {
      elkGraph.children.push({
        id: person.id,
        width: PERSON_NODE_WIDTH,
        height: PERSON_NODE_HEIGHT,
        ports: [
          {
            id: getPortId(person.id, PERSON_TOP_HANDLE_ID),
            layoutOptions: {
              'org.eclipse.elk.port.side': 'NORTH',
              'org.eclipse.elk.port.index': '0',
            },
          },
          {
            id: getPortId(person.id, PERSON_BOTTOM_HANDLE_ID),
            layoutOptions: {
              'org.eclipse.elk.port.side': 'SOUTH',
              'org.eclipse.elk.port.index': '0',
            },
          },
        ],
      });
    }
  });

  // Build set of all node IDs in the graph
  const nodeIdsInGraph = new Set(elkGraph.children.map((n) => n.id));

  // Helper: Determine which source handle a child should use based on their parentage
  function getSourceHandleForChild(
    group: (typeof marriageGroups)[number],
    childId: string
  ): string {
    const isExSpouseGroup = group.id.startsWith(
      `${MARRIAGE_GROUP_PREFIX}ex-spouses-`
    );

    const currentMarriage = sortedData.marriages.find(
      (m) => `${MARRIAGE_GROUP_PREFIX}${m.id}` === group.id
    );

    // If child is in the current marriage, check if BOTH spouses are actual parents
    if (currentMarriage?.children?.includes(childId)) {
      if (group.spouse2Id) {
        // Verify parentage if we have relationship data
        const spouse1IsParent = isActualParent(group.spouse1Id, childId);
        const spouse2IsParent = isActualParent(group.spouse2Id, childId);

        // If no relationship data, trust marriage and use couple handle
        if (spouse1IsParent === null || spouse2IsParent === null) {
          return CHILDREN_COUPLE_HANDLE_ID;
        }

        // If BOTH are actual parents, use couple handle
        if (spouse1IsParent && spouse2IsParent) {
          return CHILDREN_COUPLE_HANDLE_ID;
        }

        // Single parent scenario - use individual spouse handle
        if (spouse1IsParent && !spouse2IsParent) {
          return CHILDREN_SPOUSE_1_HANDLE_ID;
        }
        if (!spouse1IsParent && spouse2IsParent) {
          return CHILDREN_SPOUSE_2_HANDLE_ID;
        }

        // Stepchild scenario - fall through to check ex-spouse marriages
      } else {
        // No spouse2, only spouse1 exists
        const spouse1IsParent = isActualParent(group.spouse1Id, childId);
        if (spouse1IsParent !== false) {
          // spouse1 is parent or we don't know - use spouse1 handle
          return CHILDREN_SPOUSE_1_HANDLE_ID;
        }
      }
    }

    // Check if child is from spouse1's ex-spouse marriages
    const spouse1Marriages = personMarriages.get(group.spouse1Id) || [];
    for (const marriage of spouse1Marriages) {
      if (
        !marriage.isCurrent &&
        (marriage.endReason === MarriageEndReason.Divorced ||
          isExSpouseMarriage(marriage)) &&
        marriage.children?.includes(childId)
      ) {
        // Verify the ex-spouse is actually a parent (if we have relationship data)
        const exSpouseId = marriage.spouses.find(
          (id) => id !== group.spouse1Id
        );
        if (exSpouseId) {
          const exSpouseIsParent = isActualParent(exSpouseId, childId);
          const spouse1IsParent = isActualParent(group.spouse1Id, childId);

          // For ex-spouse groups, use validation to route correctly
          if (
            isExSpouseGroup &&
            exSpouseIsParent !== null &&
            spouse1IsParent !== null
          ) {
            // We have relationship data - use it to determine routing
            // If BOTH are parents, use appropriate handle based on ex-spouse count
            if (spouse1IsParent && exSpouseIsParent) {
              // Single ex-spouse co-parent scenario - use T-connector handle
              if (group.spouse1ExSpouseIds.length === 1) {
                return CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID;
              }
              // Multiple ex-spouses - use the ex-spouse bucket handle
              return CHILDREN_SPOUSE_1_EX_HANDLE_ID;
            }
            // If only spouse1 (main person) is parent, use spouse1 handle
            if (spouse1IsParent && !exSpouseIsParent) {
              return CHILDREN_SPOUSE_1_HANDLE_ID;
            }
            // If only ex-spouse is parent, use ex-spouse handle
            if (!spouse1IsParent && exSpouseIsParent) {
              return CHILDREN_SPOUSE_1_EX_HANDLE_ID;
            }
            // Neither is parent - shouldn't happen, but skip this marriage
            continue;
          }

          // For regular groups with single ex-spouse: check if both are parents
          if (
            group.spouse1ExSpouseIds.length === 1 &&
            spouse1IsParent !== false &&
            exSpouseIsParent !== false
          ) {
            // Both are likely parents (or we don't have data) - use co-parent handle
            return CHILDREN_SPOUSE_1_EX_COPARENT_HANDLE_ID;
          }

          // For regular groups or when no relationship data:
          // If we found the child in an ex-spouse marriage, assume ex-spouse handle
          if (exSpouseIsParent === null || exSpouseIsParent) {
            return CHILDREN_SPOUSE_1_EX_HANDLE_ID;
          }
        }
      }
    }

    // Check if child is from spouse2's ex-spouse marriages
    if (group.spouse2Id) {
      const spouse2Marriages = personMarriages.get(group.spouse2Id) || [];
      for (const marriage of spouse2Marriages) {
        if (
          !marriage.isCurrent &&
          (marriage.endReason === MarriageEndReason.Divorced ||
            isExSpouseMarriage(marriage)) &&
          marriage.children?.includes(childId)
        ) {
          // Verify the ex-spouse is actually a parent
          const exSpouseId = marriage.spouses.find(
            (id) => id !== group.spouse2Id
          );
          if (exSpouseId) {
            const exSpouseIsParent = isActualParent(exSpouseId, childId);
            const spouse2IsParent = isActualParent(group.spouse2Id, childId);

            // Single ex-spouse co-parent scenario for spouse2
            if (
              group.spouse2ExSpouseIds.length === 1 &&
              spouse2IsParent !== false &&
              exSpouseIsParent !== false
            ) {
              // Both are likely parents - use co-parent handle
              return CHILDREN_SPOUSE_2_EX_COPARENT_HANDLE_ID;
            }

            // If no relationship data or ex-spouse IS parent, use ex-spouse handle
            if (exSpouseIsParent === null || exSpouseIsParent) {
              return CHILDREN_SPOUSE_2_EX_HANDLE_ID;
            }
          }
        }
      }
    }

    // Default: if group has spouse2, use couple handle; otherwise use spouse1 handle
    return group.spouse2Id
      ? CHILDREN_COUPLE_HANDLE_ID
      : CHILDREN_SPOUSE_1_HANDLE_ID;
  }

  // Create edges from marriage groups to children (in sorted order for consistency)
  prunedMarriageGroups.forEach((group) => {
    // Sort children by ID for deterministic ordering
    // (ELK's model order consideration will respect this sequence)
    const sortedChildren = [...group.children].sort();

    sortedChildren.forEach((childId) => {
      // Determine which source handle to use
      const sourceHandle = getSourceHandleForChild(group, childId);

      // Check if child is in a marriage group (as spouse OR ex-spouse)
      const childGroup = prunedMarriageGroups.find(
        (g) =>
          g.spouse1Id === childId ||
          g.spouse2Id === childId ||
          g.spouse1ExSpouseIds.includes(childId) ||
          g.spouse2ExSpouseIds.includes(childId)
      );

      if (childGroup) {
        // Edge to child's marriage group using specific spouse handle
        const targetHandle = getTargetHandleForChildInGroup(
          childGroup,
          childId
        );

        // Create edge with proper source and target handles
        elkGraph.edges.push({
          id: `edge-${group.id}-${childGroup.id}`,
          sources: [getPortId(group.id, sourceHandle)],
          targets: [getPortId(childGroup.id, targetHandle)],
        });
        // Track the child's age against the group node that represents them.
        childBirthTimestampByNodeId.set(childGroup.id, birthTimestamp(childId));
      } else if (nodeIdsInGraph.has(childId)) {
        // Edge to individual child node
        elkGraph.edges.push({
          id: `edge-${group.id}-${childId}`,
          sources: [getPortId(group.id, sourceHandle)],
          targets: [getPortId(childId, PERSON_TOP_HANDLE_ID)],
        });
        childBirthTimestampByNodeId.set(childId, birthTimestamp(childId));
      }
    });
  });

  // Create edges from individual parents to children (excluding pruned people)
  sortedData.persons.forEach((person) => {
    if (
      !personsInMarriageGroups.has(person.id) &&
      !peopleToExcludeFromTree.has(person.id) &&
      nodeIdsInGraph.has(person.id)
    ) {
      const marriages = personMarriages.get(person.id) || [];
      marriages.forEach((marriage) => {
        (marriage.children || []).forEach((childId) => {
          // Check if child is in a marriage group (as spouse OR ex-spouse)
          const childGroup = prunedMarriageGroups.find(
            (g) =>
              g.spouse1Id === childId ||
              g.spouse2Id === childId ||
              g.spouse1ExSpouseIds.includes(childId) ||
              g.spouse2ExSpouseIds.includes(childId)
          );

          if (childGroup) {
            // Edge to child's marriage group using specific spouse handle
            const targetHandle = getTargetHandleForChildInGroup(
              childGroup,
              childId
            );

            // Create edge with proper handles
            elkGraph.edges.push({
              id: `edge-${person.id}-${childGroup.id}`,
              sources: [getPortId(person.id, PERSON_BOTTOM_HANDLE_ID)],
              targets: [getPortId(childGroup.id, targetHandle)],
            });
            childBirthTimestampByNodeId.set(
              childGroup.id,
              birthTimestamp(childId)
            );
          } else if (nodeIdsInGraph.has(childId)) {
            // Edge to individual child node
            elkGraph.edges.push({
              id: `edge-${person.id}-${childId}`,
              sources: [getPortId(person.id, PERSON_BOTTOM_HANDLE_ID)],
              targets: [getPortId(childId, PERSON_TOP_HANDLE_ID)],
            });
            childBirthTimestampByNodeId.set(childId, birthTimestamp(childId));
          }
        });
      });
    }
  });

  // Run ELK layout
  const layoutResult = await elk.layout(elkGraph);

  // Apply family tree post-processing:
  // - Reorder nodes within each layer based on parent position and source handle
  // - Center children under their parents for better visual balance
  // - Respect source port ordering (ex-spouse vs couple children)
  applyFamilyTreePostProcessing(
    layoutResult,
    elkGraph.edges,
    prunedMarriageGroups,
    childBirthTimestampByNodeId
  );

  // Create a map of custom node positions for quick lookup
  const customPositionMap = new Map(
    state.nodePositions?.map((pos) => [
      pos.nodeID,
      { x: pos.xPosition, y: pos.yPosition },
    ]) ?? []
  );

  // Convert to React Flow nodes
  const flowNodes: FamilyTreeNode[] = [];

  if (layoutResult.children) {
    layoutResult.children.forEach((elkNode) => {
      const nodeId = elkNode.id;

      // Use custom position if available, otherwise use ELK computed position
      const customPosition = customPositionMap.get(nodeId);
      const x = customPosition?.x ?? (elkNode.x || 0);
      const y = customPosition?.y ?? (elkNode.y || 0);

      if (nodeId.startsWith(MARRIAGE_GROUP_PREFIX)) {
        const group = prunedMarriageGroups.find((g) => g.id === nodeId);
        if (group) {
          const spouse1 = personMap.get(group.spouse1Id);
          const spouse2 = group.spouse2Id
            ? personMap.get(group.spouse2Id)
            : undefined;

          if (spouse1) {
            // Get ex-spouse person data
            const spouse1ExSpouses = group.spouse1ExSpouseIds
              .map((id) => personMap.get(id))
              .filter((p): p is NonNullable<typeof p> => p !== undefined);

            const spouse2ExSpouses = group.spouse2ExSpouseIds
              .map((id) => personMap.get(id))
              .filter((p): p is NonNullable<typeof p> => p !== undefined);

            flowNodes.push({
              id: nodeId,
              type: 'marriageGroup',
              position: { x, y },
              data: {
                id: group.id,
                spouse1,
                spouse2,
                spouse1ExSpouses,
                spouse2ExSpouses,
                hasCoupleChildren: group.hasCoupleChildren ?? false,
                hasSpouse1ExCoparentChildren:
                  group.hasSpouse1ExCoparentChildren ?? false,
                hasSpouse2ExCoparentChildren:
                  group.hasSpouse2ExCoparentChildren ?? false,
                allChildren: group.children,
                isCurrentGroup: true,
              },
              width: elkNode.width,
              height: elkNode.height,
            });
          }
        }
      } else {
        const person = personMap.get(nodeId);
        if (person) {
          flowNodes.push({
            id: nodeId,
            type: 'person',
            position: { x, y },
            data: { person },
            width: elkNode.width,
            height: undefined,
            // Synthetic nodes must not capture pointer events, otherwise they can
            // block clicks/hovers on nodes that are visually behind them
            ...(person.isSynthetic && {
              style: { pointerEvents: 'none' as const },
            }),
          });
        }
      }
    });
  }

  // Convert edges to React Flow format with proper handle IDs
  const rfEdges: Edge[] = elkGraph.edges.map((e) => {
    const sourcePort = e.sources?.[0] || '';
    const targetPort = e.targets?.[0] || '';
    const sourceParts = sourcePort.split('::');
    const targetParts = targetPort.split('::');

    const sourceId = sourceParts[0] || '';
    const targetId = targetParts[0] || '';

    // Check if either source or target is a synthetic node
    const sourcePerson = personMap.get(sourceId);
    const targetPerson = personMap.get(targetId);
    const isSyntheticEdge =
      sourcePerson?.isSynthetic || targetPerson?.isSynthetic;

    const edge: Edge = {
      id: e.id,
      source: sourceId,
      target: targetId,
      sourceHandle: sourceParts[1], // Include handle ID for React Flow
      targetHandle: targetParts[1], // Include handle ID for React Flow
      type: 'arrow' as const,
      data: {
        hideArrow: false,
        hideLabel: false,
        endMarkerType: 'circle' as const,
      },
      selectable: false,
      // Mark synthetic edges with a className instead of opacity
      // so they don't get filtered out during BFS connectivity checks
      className: isSyntheticEdge ? 'synthetic-edge' : undefined,
    };

    return edge;
  });

  // Filter out orphaned nodes (nodes not connected to primary clients)
  const { filteredNodes, filteredEdges } = filterOrphanedNodes(
    flowNodes,
    rfEdges,
    sortedData.persons
  );

  return { nodes: filteredNodes, edges: filteredEdges };
}
